import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';


@Component({
  selector: 'lib-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  @Output()
  modalClose = new EventEmitter<void>();

  @Input() cssClass = '';
  @Input() headerClass = '';
  @Input() bodyClass = '';
  @Input() closable = true;

  startY = 0;
  currentTranslateY = 0;
  threshold = 150;
  animateBack = false;
  slideDown = false;

  @ContentChild('body')
  protected bodyTemplate!: TemplateRef<unknown>;

  @ContentChild('title')
  protected titleTemplate!: TemplateRef<unknown>;

  @ViewChild('backDropRef')
  protected backDropRef!: ElementRef<HTMLElement>;

  protected isOpen = false;

  private readonly document = inject(DOCUMENT);

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly nativeElement = this.elementRef.nativeElement;

  private previousBodyOverflow = '';

  constructor() {
    this.nativeElement.style.display = 'none';
  }

  ngAfterViewInit(): void {
    this.document.body.appendChild(this.nativeElement);
  }

  open(): void {
    this.previousBodyOverflow = this.document.body.style.overflow;

    this.resetModalState();

    this.nativeElement.style.display = 'block';
    this.isOpen = true;

    this.document.body.style.overflow = 'hidden';
  }

  close(): void {
    if (!this.closable) {
      return;
    }

    this.nativeElement.style.display = 'none';
    this.isOpen = false;

    this.document.body.style.overflow = this.previousBodyOverflow;

    this.modalClose.emit();
  }

  closeFromBackDrop(event: Event): void {
    const targetElement = event.target as HTMLElement;
    const backDropElement = this.backDropRef?.nativeElement;

    if (!backDropElement || targetElement !== backDropElement) {
      return;
    }

    this.close();
  }

  onTouchStart(event: TouchEvent): void {
    if (!this.closable) {
      return;
    }

    this.startY = event.touches[0].clientY;
    this.currentTranslateY = 0;
    this.animateBack = false;
    this.slideDown = false;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.closable) {
      return;
    }

    const touchY = event.touches[0].clientY;
    const deltaY = touchY - this.startY;

    if (deltaY > 0) {
      event.preventDefault();
      this.currentTranslateY = deltaY;
    }
  }

  onTouchEnd(): void {
    if (!this.closable) {
      return;
    }

    if (this.currentTranslateY > this.threshold) {
      this.slideDown = true;
      return;
    }

    this.animateBack = true;
    this.currentTranslateY = 0;
  }

  onTransitionEnd(): void {
    if (this.slideDown) {
      this.close();
    }

    this.resetModalState();
  }

  resetModalState(): void {
    this.currentTranslateY = 0;
    this.slideDown = false;
    this.animateBack = false;
  }

  ngOnDestroy(): void {
    this.document.body.style.overflow = this.previousBodyOverflow;
    this.nativeElement.remove();
  }
}
